<template>
    <section class="ResolutionSummary">
        <header class="ResolutionSummary__header">
            <h3 class="ResolutionSummary__title">
                Resolution
                <span v-if="isReverted" class="ResolutionSummary__statusBadge">
                    Withdrawn
                </span>
            </h3>
            <div class="ResolutionSummary__actions">
                <slot name="actions" />
            </div>
        </header>
        <template v-if="!resolution">
            <p class="ResolutionSummary__unavailable">
                {{ unavailableMessage }}
            </p>
            <p class="ResolutionSummary__unavailableExplanation">
                {{ unavailableExplanation }}
            </p>
        </template>
        <dl v-else class="ResolutionSummary__facts">
            <div class="ResolutionSummary__fact">
                <dt class="ResolutionSummary__label">
                    Resolved by
                </dt>
                <dd class="ResolutionSummary__value ResolutionSummary__resolvedBy">
                    {{ resolvedByLabel }}
                </dd>
            </div>
            <div class="ResolutionSummary__fact">
                <dt class="ResolutionSummary__label">
                    Resolved on
                </dt>
                <dd class="ResolutionSummary__value ResolutionSummary__resolvedAt">
                    {{ resolvedAt }}
                </dd>
            </div>
            <div class="ResolutionSummary__fact">
                <dt class="ResolutionSummary__label">
                    Outcome
                </dt>
                <dd class="ResolutionSummary__value ResolutionSummary__outcome">
                    {{ outcomeLabel }}
                </dd>
            </div>
            <div v-if="decisionLines.length > 0" class="ResolutionSummary__fact">
                <dt class="ResolutionSummary__label">
                    Decision
                </dt>
                <dd class="ResolutionSummary__value ResolutionSummary__decision">
                    <!-- Keyed by index: two requirements can legitimately produce the same line, and keying by
                         content would collapse them into one. -->
                    <div v-for="(line, index) in decisionLines" :key="index">
                        {{ line }}
                    </div>
                </dd>
            </div>
            <template v-if="resolution.revertedAt != null">
                <div class="ResolutionSummary__fact">
                    <dt class="ResolutionSummary__label">
                        Reverted by
                    </dt>
                    <dd class="ResolutionSummary__value ResolutionSummary__revertedBy">
                        {{ resolution.revertedBy }}
                    </dd>
                </div>
                <div class="ResolutionSummary__fact">
                    <dt class="ResolutionSummary__label">
                        Reverted on
                    </dt>
                    <dd class="ResolutionSummary__value ResolutionSummary__revertedAt">
                        {{ revertedAt }}
                    </dd>
                </div>
                <div v-if="resolution.revertReason != null" class="ResolutionSummary__fact">
                    <dt class="ResolutionSummary__label">
                        Revert reason
                    </dt>
                    <dd class="ResolutionSummary__value ResolutionSummary__revertReason">
                        {{ resolution.revertReason }}
                    </dd>
                </div>
            </template>
        </dl>
    </section>
</template>

<script lang="ts">
    import { defineComponent, PropType } from 'vue';

    import {
        ActionType,
        ApiActionResolution,
    } from '@evidentid/tprm-portal-lib/models/entity-actions-review';

    import {
        RESOLUTION_UNAVAILABLE_EXPLANATION,
        RESOLUTION_UNAVAILABLE_MESSAGE,
        RESOLVED_BY_UNRECORDED,
    } from '@/modules/entity-actions-review/constants';
    import {
        getResolutionResultSummary,
    } from '@/modules/entity-actions-review/utils/getResolutionResultSummary';
    import { formatDateTime } from '@/utils/formatDateTime';

    const TIMESTAMP_FORMAT = 'MMM D, YYYY h:mm A';

    export default defineComponent({
        name: 'ResolutionSummary',
        props: {
            /* Null for every action completed before the resolution audit record existed. Those
               rows make up most of the Completed tab at launch, so this is a normal state the
               panel explains rather than a missing-data error. Only the resolver and the payload
               are unknown -- the action itself is still revertable. */
            resolution: {
                type: Object as PropType<ApiActionResolution | null>,
                default: null,
            },
            actionType: {
                type: String as PropType<ActionType>,
                required: true,
            },
        },
        data() {
            return {
                unavailableMessage: RESOLUTION_UNAVAILABLE_MESSAGE,
                unavailableExplanation: RESOLUTION_UNAVAILABLE_EXPLANATION,
            };
        },
        computed: {
            resolvedAt(): string {
                return formatDateTime(this.resolution?.resolvedAt, TIMESTAMP_FORMAT);
            },
            /* An action decided before the audit record existed has a resolution row only once it is
               reverted, and that row knows when but not who. Naming the gap beats an empty cell. */
            resolvedByLabel(): string {
                return this.resolution?.resolvedBy || RESOLVED_BY_UNRECORDED;
            },
            revertedAt(): string {
                return formatDateTime(this.resolution?.revertedAt, TIMESTAMP_FORMAT);
            },
            isReverted(): boolean {
                return this.resolution?.revertedAt != null;
            },
            outcomeLabel(): string {
                let label = '—';
                if (this.resolution?.outcome === 'accepted') {
                    label = 'Accepted';
                } else if (this.resolution?.outcome === 'rejected') {
                    label = 'Rejected';
                }
                /* The outcome alone would otherwise read as the decision still standing, so a
                   reverted resolution says so right where the outcome is displayed rather than
                   leaving the withdrawal to the badge in the header alone. */
                return this.isReverted ? `${label} (withdrawn)` : label;
            },
            decisionLines(): string[] {
                return getResolutionResultSummary(this.actionType, this.resolution?.result);
            },
        },
    });
</script>
